import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="ui--turbo-modal"
// Manages modals loaded via Turbo Frames
export default class extends Controller {
  static targets = ["frame"]

  connect() {
    this.boundKeydown = this.handleKeydown.bind(this)
    document.addEventListener("keydown", this.boundKeydown)

    // Listen for turbo:frame-load to show modal when content arrives
    this.boundFrameLoad = this.handleFrameLoad.bind(this)
    this.frameTarget.addEventListener("turbo:frame-load", this.boundFrameLoad)

    // Handle clicks via event delegation for ui--modal#close actions
    this.boundClick = this.handleClick.bind(this)
    this.frameTarget.addEventListener("click", this.boundClick)

    // Detect when frame is emptied via turbo_stream.update (does not fire turbo:frame-load)
    this.observer = new MutationObserver(() => {
      if (!this.frameTarget.querySelector("[data-ui--modal-target='container']")) {
        this.unlockScroll()
      }
    })
    this.observer.observe(this.frameTarget, { childList: true })
  }

  disconnect() {
    document.removeEventListener("keydown", this.boundKeydown)
    this.frameTarget.removeEventListener("turbo:frame-load", this.boundFrameLoad)
    this.frameTarget.removeEventListener("click", this.boundClick)
    this.observer.disconnect()
    this.unlockScroll()
  }

  handleFrameLoad() {
    // Find the modal container inside the frame
    const modalContainer = this.frameTarget.querySelector("[data-ui--modal-target='container']")
    if (modalContainer) {
      modalContainer.classList.remove("hidden")
      this.lockScroll()
      this.focusFirstElement(modalContainer)
    }
  }

  handleClick(event) {
    // Handle clicks that would normally go to ui--modal#close
    const action = event.target.closest("[data-action*='ui--modal#close']")
    if (action) {
      event.preventDefault()
      this.close(event)
      return
    }

    // Close when clicking outside the modal panel (on backdrop/wrapper area)
    const modalPanel = this.frameTarget.querySelector("[data-ui--modal-target='panel']")
    if (modalPanel && !modalPanel.contains(event.target)) {
      this.close(event)
    }
  }

  close(event) {
    event?.preventDefault()
    const modalContainer = this.frameTarget.querySelector("[data-ui--modal-target='container']")
    if (modalContainer) {
      modalContainer.classList.add("hidden")
    }
    this.unlockScroll()
    // Clear the frame content
    this.frameTarget.innerHTML = ""
  }

  handleKeydown(event) {
    if (event.key === "Escape") {
      const modalContainer = this.frameTarget.querySelector("[data-ui--modal-target='container']")
      if (modalContainer && !modalContainer.classList.contains("hidden")) {
        this.close(event)
      }
    }
  }

  lockScroll() {
    document.body.style.overflow = "hidden"
  }

  unlockScroll() {
    document.body.style.overflow = ""
  }

  focusFirstElement(container) {
    // Double requestAnimationFrame ensures DOM is fully rendered after removing hidden class
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        // Prioritize form inputs over buttons
        const input = container.querySelector('input:not([type="hidden"]), select, textarea')
        if (input) {
          input.focus()
          return
        }

        const focusable = container.querySelector(
          'button, [href], [tabindex]:not([tabindex="-1"])'
        )
        focusable?.focus()
      })
    })
  }
}
